<script setup>
import { storeToRefs } from "pinia";
import { ref, computed, onBeforeMount, onMounted } from "vue";
import AgentLayout from "./layouts/AgentLayout.vue";
import PageHeader from "@/components/shared/PageHeader.vue";
import StatisticsService from "@/services/statistics";
import {
  DxTreeList,
  DxColumn,
  DxColumnChooser,
  DxHeaderFilter,
  DxToolbar,
  DxItem,
  // DxFilterRow,
  DxScrolling,
  DxLoadPanel,
  DxStateStoring,
  DxSelection,
  DxSearchPanel,
} from "devextreme-vue/tree-list";
// import notify from 'devextreme/ui/notify';
// import { DxTooltip } from 'devextreme-vue/tooltip';
// import DxDropDownButton from "devextreme-vue/drop-down-button";
// import { default as Button } from "devextreme-vue/button";
import { useToast } from "vue-toastification";
import checkUserPassword from "../components/users/checkUserPassword.vue";

import addStore from "../components/stores/addStore.vue";
import addLocation from "../components/locations/addLocation.vue";
import addDevice from "../components/kiosks/addDevice.vue";
// import addDeviceWithDetails from "../components/kiosks/addDeviceWithDetails.vue";
// import addDeviceWithGenerateToken from "../components/kiosks/addDeviceWithGenerateToken.vue";

import editStore from "../components/stores/editStore.vue";
import deleteStore from "../components/stores/deleteStore.vue";
import setSitePassword from "../components/stores/setSitePassword.vue";
import passwordHistory from "../components/stores/passwordHistory";
import editLocation from "../components/locations/editLocation.vue";
import moveLocation from "../components/locations/moveLocation.vue";
import deleteLocation from "../components/locations/deleteLocation.vue";

import editDevice from "../components/kiosks/editDevice.vue";
import openDoor from "../components/kiosks/openDoor.vue";
import restartDevice from "../components/kiosks/restartDevice.vue";
import installApp from "../components/kiosks/installApp.vue";
import uninstallApps from "../components/kiosks/uninstallApps.vue";
import deviceInfo from "../components/kiosks/deviceInfo.vue";
import moveDevice from "../components/kiosks/moveDevice.vue";
import restartApp from "../components/kiosks/restartApp.vue";
import refreshFaceLibrary from "../components/kiosks/refreshFaceLibrary.vue";
import previewLogs from "../components/kiosks/previewLogs.vue";
import deRegisterDevice from "../components/kiosks/deRegisterDevice.vue";
import deviceSettings from "../components/kiosks/deviceSettings.vue";
import remoteControl from "../components/kiosks/remoteControl.vue";
import deviceToken from "../components/kiosks/deviceToken.vue";
import deviceConfiguration from "../components/kiosks/deviceConfiguration.vue";
import deviceApps from "../components/kiosks/deviceApps.vue";
import DiskChart from "./partials/sitesAndDevices/DiskChart.vue";
// import SiteActionCell from "../components/sites/SiteActionCell.vue";
// import AreaActionCell from "../components/areas/AreaActionCell.vue";
// import DeviceActionCell from "../components/kiosks/DeviceActionCell.vue";
import moment from "moment";
import DeviceInfoSkeletor from "./partials/sitesAndDevices/skeleton/DeviceInfoSkeleton.vue";
import DiskInfoSkeletor from "./partials/sitesAndDevices/skeleton/DiskSectionSkeleton.vue";
import BatterySkeletor from "./partials/sitesAndDevices/skeleton/BatterySectionSkeleton.vue";
import MemorySkeletor from "./partials/sitesAndDevices/skeleton/MemorySectionSkeleton.vue";
import KioskActionsBarSkeletor from "./partials/sitesAndDevices/skeleton/KioskActionSectionSkeleton.vue";

import applyJob from "../components/jobs/applyJob.vue";
import useValidation from "@/utils/helpers/validations";

const { checkLoggedInUserRole } = useValidation();

import { useSitesStore } from "@/stores/sites";
import { useUiStore } from "@/stores/ui";
import { useDevicesStore } from "@/stores/devices";
import { useAgentsStore } from "@/stores/agents";
import { useGridPreferencesStore } from "@/stores/gridPreferences";
import { useStatisticsStore } from "@/stores/statistics";
import { useAppsStore } from "@/stores/apps";
import { useConfigsStore } from "@/stores/configs";
import { useCompanyStore } from "@/stores/company";
const availableKioskActions = [
  {
    action: "refresh_device_info",
    text: "Refresh",
    icon: "bi-arrow-repeat",
  },
  {
    action: "restart_device",
    text: "Restart",
    icon: "bi-phone-flip",
  },
  {
    action: "device_apps",
    text: "Get Apps",
    icon: "bi-columns-gap",
  },
  {
    action: "remote_control",
    text: "Remote",
    icon: "bi-pc-display-horizontal",
  },
];

const sitesStore = useSitesStore();
const statisticsStore = useStatisticsStore();
const appsStore = useAppsStore();
const configsStore = useConfigsStore();
const uiStore = useUiStore();
const devicesStore = useDevicesStore();
const agentsStore = useAgentsStore();
const gridPreferencesStore = useGridPreferencesStore();
const toast = useToast();
let getModalState = computed(() => uiStore.modalState);
const { companyId } = storeToRefs(useCompanyStore());
const adminPreferenceId = computed(() =>
  localStorage.getItem("adminPreferenceId")
);
const user = ref(JSON.parse(localStorage.getItem("userDetails")));
// const userEmail = ref(JSON.parse(localStorage.getItem("user"))); // only used by the commented-out "Refresh Remote Integration" menu item below
// const currentCompany = ref(JSON.parse(localStorage.getItem("currentCompany")));
const hideSkeletor = computed(
  () => deviceData.value && !fetchDeviceDataLoading.value && showSideInfo.value
);
// const showKioskActionBar = computed(()=>showSideInfo.value && deviceData.value && !fetchDeviceDataLoading.value)
const dataGridTreeRefName = ref(null);
const allowedOperations = ref(["contains"]);
// const buttonData = ref(["Excel", "CSV", "PDF"]);
const expandedRowKeys = ref([1, 2]);
const selectedRowKeys = ref([1, 29, 42]);
let sitesGrid = ref([]);
let sitesOptions = ref([]);
let appsOptions = ref([]);
let configsOptions = ref([]);
let agentsOptions = ref([]);
let showClearAll = ref(false);
const addMenuOpen = ref(false);
const addStoreVisible = ref(false);
let showSideInfo = ref(false);
let showMassAction = ref(true);
let deviceData = ref({ deviceInfo: [] });
let fetchDeviceDataLoading = ref(false);
let ifDisableParentNode = ref(false);
let isSelectedDevices = ref(true);
const selectedDevicesCount = ref(0);
let devicesNames = ref([]);
let mac_addresses = ref([]);
let deviceDataObj = ref({});
const selectionMode = ref("all");

const visibleDevicesCount = computed(
  () => sitesGrid.value.filter((row) => row.data_type === "device").length
);

// Statistics state
const totalSites = ref(0);
const totalDevices = ref(0);
const totalOnlineDevices = ref(0);
const totalOfflineDevices = ref(0);
const totalPendingDevices = ref(0);

const dataTreeGrid = computed(() => {
  return dataGridTreeRefName.value?.instance || null;
});

const storeModal = () => {
  addStoreVisible.value = true;
};


const locationModal = async () => {
  // await setModalState();
  // if (type == "show") state.areaModal.show();
  // else state.areaModal.hide();
  action("add_location", "");
};

const kioskModal = async () => {
  action("add_kiosk", "");
};

const contentReady = (e) => {
  let sortedExp = getSortedColumns();
  let filterExp = e.component.getCombinedFilter();
  if (filterExp || sortedExp) {
    showClearAll.value = true;
  } else {
    showClearAll.value = false;
  }
  setColumnChooserPosition();
};

const getSortedColumns = () => {
  if (dataTreeGrid.value) {
    let allColumns = Array.from(
      Array(dataTreeGrid.value.columnCount()).keys()
    ).map((index) => dataTreeGrid.value.columnOption(index));
    allColumns = allColumns.find((col) => col.sortIndex != null);
    return allColumns;
  }
  return null;
};

const setColumnChooserPosition = () => {
  if (dataTreeGrid.value) {
    var columnChooserView = dataTreeGrid.value.getView("columnChooserView");
    if (columnChooserView && !columnChooserView._popupContainer) {
      columnChooserView._initializePopupContainer();
      columnChooserView.render();
    }
    columnChooserView._popupContainer.option("position", {
      of: dataTreeGrid.value.element(),
      my: "left top",
      at: "left top",
      offset: "0 50",
    });
  }
};

const onEditorPreparing = (e) => {
  if (e.parentType == "filterRow") {
    e.editorOptions.placeholder = "Search";
  }
};

const onFocusedRowChanged = async () => {
  // if (
  //   e.row?.data?.data_type == "device" &&
  //   e.row?.data?.mac_address &&
  //   e.row?.data?.mac_address != "-" &&
  //   e.row?.isSelected === false &&
  //   e.row?.isExpanded === false
  // ) {
  //   //  //   }

  //   showSideInfo.value = false;
  //   // showSideInfo.value = true;
  //   deviceData.value = { deviceInfo: [] };
  //   deviceDataObj.value = e.row.data;
  //   await store.commit("setDeviceDataObject", deviceDataObj.value);
  //   fetchDeviceInfo(e.row?.data);
  // } else if (
  //   e.row?.data?.data_type == "device" &&
  //   e.row?.data?.mac_address == "-" &&
  //   e.row?.isSelected === false &&
  //   e.row?.isExpanded === false
  // ) {
  //   toast.error("Please add mac address for this device");
  //   showSideInfo.value = false;
  // } else {
  //   showSideInfo.value = false;
  // }
};

const onClickedRowChanged = () => {};

// Move and De-register only change our own records — they take effect
// immediately whether or not the device is connected, so the "once they are
// online or reconnected" warning is misleading for them. Every other action
// sends a command the device must be online to receive, so it keeps the warning.
const OFFLINE_AGNOSTIC_ACTIONS = new Set([
  "move_device",
  "mass_move_devices",
  "deregister_device",
  "mass_deregister_devices",
]);

const getSelectedData = (mass_action) => {
  let offlineDevicesData = [];
  let rows = dataTreeGrid.value.getSelectedRowsData(selectionMode.value);
  const devicesData = rows.filter((row) => {
    if (row.data_type == "device") {
      if (row.is_online == 0) {
        offlineDevicesData.push(row);
      }
      return row;
    }
  });
  if (offlineDevicesData.length && !OFFLINE_AGNOSTIC_ACTIONS.has(mass_action)) {
    toast.warning(
      "Action will be applied on all device(s) once they are online or reconnected"
    );
  }

  action(mass_action, devicesData);
};

const onSelectionChanged = async (e) => {
  let isFoundDevice = false;
  isSelectedDevices.value = true;
  selectedDevicesCount.value = 0;
  if (e && e.selectedRowsData && e.selectedRowsData.length) {
    let rows = dataTreeGrid.value.getSelectedRowsData(selectionMode.value);
    const deviceRows = rows.filter((obj) => obj.data_type === "device");
    selectedDevicesCount.value = deviceRows.length;
    if (deviceRows.length) {
      isSelectedDevices.value = false;
      showSideInfo.value = false;
      isFoundDevice = true;
    }
    if (!isFoundDevice)
      toast.warning(
        "You should select at least one device to use mass actions"
      );
  } else {
    isSelectedDevices.value = true;
  }
};

const clearFilter = () => {
  if (dataTreeGrid.value) {
    dataTreeGrid.value.clearFilter("row");
    dataTreeGrid.value.clearFilter("header");
    dataTreeGrid.value.clearFilter("search");
    dataTreeGrid.value.clearSorting();
  }
};

const showColumnChooser = () => {
  if (dataTreeGrid.value) {
    dataTreeGrid.value.showColumnChooser();
  }
};

const fetchStatistics = async () => {
  try {
    if(companyId.value) {
      const response = await StatisticsService.getCompanyStatistics(companyId.value);
      if(response && response.data) {
        const data = response.data;
        totalSites.value = data.sites?.total ?? 0;
        totalDevices.value = data.devices?.total ?? 0;
        totalOnlineDevices.value = data.devices?.online ?? 0;
        totalOfflineDevices.value = data.devices?.offline ?? 0;
        totalPendingDevices.value = data.devices?.pending ?? 0;
      }
    }
  } catch (error) {
    console.error("Error fetching statistics:", error);
  }
};

const fetchAllSites = () => {
  sitesStore
    .fetchSites(companyId.value)
    .then((res) => {
      sitesOptions.value = res.data;
    })
    .catch(() => {});
};

const refreshTreeListGrid = () => {
  fetchStoresGrid();
};

const refreshTreeList = () => {
  dataTreeGrid.value.beginCustomLoading();
  fetchStoresGrid();
};

const customLoad = async () => {
  await gridPreferencesStore
    .onTableHeader({
      url: "/stores-and-kiosks-grids",
      companyId: companyId.value,
    })
    .catch(() => {});
  let value = gridPreferencesStore.tableHeaders;
  return value;
};

const OnCellPrepared = (e) => {
  if (
    ifDisableParentNode.value &&
    e.row?.rowType == "data" &&
    e.cellElement.className
  ) {
    if (
      (e.data?.data_type === "site" || e.data?.data_type === "area") &&
      e.cellElement.className === "dx-treelist-cell-expandable"
    ) {
      const checkbox = e.cellElement.querySelector(".dx-select-checkbox");
      if (checkbox) checkbox.hidden = true;
    }
  }
};

const customSave = (gridState) => {
  gridState.pageIndex = 0;
  gridState.pageSize = 5;
  gridState.focusedRowKey = "";

  let value = JSON.stringify(gridState);
  value = value.replace(',"selectedRowKeys":[]', "");
  if (!gridPreferencesStore.tableHeaders) {
    gridPreferencesStore
      .createTableHeaders({
        url: "/stores-and-kiosks-grids",
        headers: value,
        filters: "[]",
        companyId: companyId.value,
      })
      .catch(() => {});
  } else if (value != JSON.stringify(gridPreferencesStore.tableHeaders)) {
    gridPreferencesStore
      .updateTableHeaders({
        url: "/stores-and-kiosks-grids",
        headers: value,
        filters: "[]",
        companyId: companyId.value,
        adminPreferenceId: adminPreferenceId.value,
      })
      .catch(() => {});
  }};

const onItemClick = (type) => {
  // let type = null;
  // if (e.itemData == "Excel") {
  //   type = 'excel';
  // } else if (e.itemData == "CSV") {
  //   type = 'csv';
  // } else if (e.itemData == "PDF") {
  //   type = 'pdf';
  // }

  statisticsStore
    .exportSitesGrid({ companyId: companyId.value, type: type })
    .then(() => {
    })
    .catch(() => {});
};

const fetchDeviceInfo = (data) => {
  fetchDeviceDataLoading.value = true;
  const mainCategories = ["network", "disk", "memory", "battery"];

  let payload = {
    entityId: companyId.value,
    kioskName: data.name,
    macAddress: data.mac_address,
    userName: user.value.first_name + " " + user.value.last_name,
    userId: user.value.user_id,
    connectionType: "daemon",
  };

  devicesStore
    .fetchDeviceInfo(payload)
    .then((res) => {
      let dataInfo = JSON.parse(res.data);
      delete dataInfo.manufacturer;
      Object.keys(dataInfo).forEach((key) => {
        if (mainCategories.includes(key))
          Object.assign(deviceData.value, { [key]: dataInfo[key] });
        else deviceData.value.deviceInfo.push({ [key]: dataInfo[key] });
      });
      fetchDeviceDataLoading.value = false;
    })
    .catch(() => {
      fetchDeviceDataLoading.value = false;
      showSideInfo.value = false;
      deviceDataObj.value = {};
    });
};

// Only used by the device context-menu items currently commented out below.
// const checkDeviceMenuItems = (data) => {
//   if (data.mac_address && data.mac_address != "-") return true;
//   else return false;
// };

const action = async (action, data) => {
  if (data.data_type == "device" && !data.is_online) {
    if (action == "remote_control") {
      toast.warning("The device is offline, you can't use remote control");
      return;
    } else if (!OFFLINE_AGNOSTIC_ACTIONS.has(action)) {
      toast.warning(
        "Action will be applied on device once it's online or reconnected"
      );
    }
  }
  await uiStore.setModalAction(action);
  uiStore.setModalState(true);
  await uiStore.setModalData(data);

  if (action == "screenshot" || action == "mass_screenshots") {
    if (Array.isArray(data)) {
      data.map((row) => {
        devicesNames.value.push(row.name);
        mac_addresses.value.push(row.mac_address);
      });
    }

    devicesStore[action == "screenshot" ? "fetchScreenshot" : "fetchMassScreenshot"](
        {
          entityId: companyId.value,
          kioskName: action == "screenshot" ? data.name : devicesNames.value,
          macAddress: action == "screenshot" ? data.mac_address : "",
          macAddresses: action == "mass_screenshots" ? mac_addresses.value : "",
          userName: user.value.first_name + " " + user.value.last_name,
          userId: user.value.user_id,
          connectionType: "daemon",
        }
      )
      .then(() => {
        resetStoreData();
      })
      .catch(() => {
      });
  } else if (action == "refresh_get_screen") {
    if (Array.isArray(data)) {
      data.map((row) => {
        devicesNames.value.push(row.name);
        mac_addresses.value.push(row.mac_address);
      });
    } else {
      devicesNames.value.push(data.name);
      mac_addresses.value.push(data.mac_address);
    }

    devicesStore
      .fetchDeviceRefreshGetScreen({
        entityId: companyId.value,
        kioskName: devicesNames.value,
        macAddresses: mac_addresses.value,
        userName: user.value.first_name + " " + user.value.last_name,
        userId: user.value.user_id,
        connectionType: "daemon",
      })
      .then(() => {
        resetStoreData();
      })
      .catch(() => {
      });
  } else if (action == "refresh_device_info") {
    showSideInfo.value = true;
    deviceData.value = { deviceInfo: [] };
    deviceDataObj.value = data;
    await devicesStore.setDeviceDataObject(deviceDataObj.value);
    fetchDeviceInfo(data);
  }
};

const resetStoreData = () => {
  setTimeout(() => {
    uiStore.setModalState(false);
    uiStore.setModalData({});
    uiStore.setModalAction(null);
  }, 400);
};

const closeSideInfo = () => {
  deviceDataObj.value = {};
  deviceData.value = { deviceInfo: [] };
  showSideInfo.value = false;
};

const addMenuItems = (e) => {
  if (e.target == "content" && e.row?.node?.data?.data_type == "site") {
    // e.items can be undefined
    if (!e.items) e.items = [];
    // Add a custom menu item
    e.items.push(
      {
        html: '<i class="bi bi-pencil-square"></i> Edit',
        onItemClick: () => {
          action("edit_store", e.row?.node?.data);
        },
      },
      {
        html: '<i class="bi bi-gear"></i> Apply Config',
        onItemClick: () => {
          action("site_config", e.row?.node?.data);
        },
      },
      {
        html: '<i class="bi bi-trash3"></i> Delete',
        cssClass: 'menu-item-danger',
        beginGroup: true,
        onItemClick: () => {
          action("delete_store", e.row?.node?.data);
        },
      },
      {
        html: '<i class="bi bi-building-add"></i> Add Location',
        onItemClick: () => {
          action("add_location_with_site", e.row?.node?.data);
        },
      },
      {
        html: '<i class="bi bi-key"></i> Change Password',
        onItemClick: () => {
          action("check_user_password", e.row?.node?.data);
        },
      }
    );
  } else if (e.target == "content" && e.row?.node?.data?.data_type == "area") {
    // e.items can be undefined
    if (!e.items) e.items = [];
    // Add a custom menu item
    e.items.push(
      {
        html: '<i class="bi bi-pencil-square"></i> Edit',
        onItemClick: () => {
          action("edit_location", e.row?.node?.data);
        },
      },
      {
        html: '<i class="bi bi-arrow-left-right"></i> Move',
        onItemClick: () => {
          action("move_location", e.row?.node?.data);
        },
      },
      {
        html: '<i class="bi bi-gear"></i> Apply Config',
        onItemClick: () => {
          action("area_config", e.row?.node?.data);
        },
      },
      {
        html: '<i class="bi bi-trash3"></i> Delete',
        cssClass: 'menu-item-danger',
        beginGroup: true,
        onItemClick: () => {
          action("delete_location", e.row?.node?.data);
        },
      },
      {
        html: '<i class="bi bi-tablet"></i> Add Kiosk',
        // visible : checkLoggedInUserRole(),
        onItemClick: () => {
          action("add_kiosk", e.row?.node?.data);
        },
      }
    );
  } else if (
    e.target == "content" &&
    e.row?.node?.data?.data_type == "device"
  ) {
    // e.items can be undefined
    if (!e.items) e.items = [];
    // Add a custom menu item
    e.items.push(
      {
        html: '<i class="bi bi-pencil-square"></i> Edit',
        onItemClick: () => {
          action("edit_device", e.row?.node?.data);
        },
      },
      // {
      //     html: '<i class="bi bi-door-open"></i> Open Door',
      //     onItemClick: () => {
      //       action('open_door', e.row?.node?.data);
      //     }
      // },
      // {
      //   html: '<i class="bi bi-columns-gap"></i> Get Apps',
      //   visible: checkDeviceMenuItems(e.row?.node?.data),
      //   onItemClick: () => {
      //     action("device_apps", e.row?.node?.data);
      //   },
      // },
      // {
      //   html: '<i class="bi bi-phone-flip"></i> Restart Device',
      //   visible: checkDeviceMenuItems(e.row?.node?.data),
      //   onItemClick: () => {
      //     action("restart_device", e.row?.node?.data);
      //   },
      // },
      // {
      //   html: '<i class="bi bi-fullscreen"></i> Screenshot',
      //   visible: checkDeviceMenuItems(e.row?.node?.data),
      //   onItemClick: () => {
      //     action("screenshot", e.row?.node?.data);
      //   },
      // },
      {
        html: '<i class="bi bi-arrow-left-right"></i> Move',
        onItemClick: () => {
          action("move_device", e.row?.node?.data);
        },
      },
      // {
      //   html: '<i class="bi bi-gear"></i> Apply Config',
      //   visible: checkDeviceMenuItems(e.row?.node?.data),
      //   onItemClick: () => {
      //     action("device_config", e.row?.node?.data);
      //   },
      // },
      // {
      //   html: '<i class="bi bi-list-check"></i> Apply Job',
      //   visible: checkDeviceMenuItems(e.row?.node?.data),
      //   onItemClick: () => {
      //     action("apply_job", e.row?.node?.data);
      //   },
      // },
      // {
      //   html: '<i class="bi bi-arrow-counterclockwise"></i> Restart App',
      //   visible: checkDeviceMenuItems(e.row?.node?.data),
      //   onItemClick: () => {
      //     action("restart_app", e.row?.node?.data);
      //   },
      // },
      // {
      //   html: '<i class="bi bi-download"></i> Install App',
      //   visible: checkDeviceMenuItems(e.row?.node?.data),
      //   onItemClick: () => {
      //     action("install_app", e.row?.node?.data);
      //   },
      // },
      // {
      //   html: '<i class="bi bi-list-columns"></i> Preview Logs',
      //   visible: checkDeviceMenuItems(e.row?.node?.data),
      //   onItemClick: () => {
      //     action("preview_logs", e.row?.node?.data);
      //   },
      // },
      // {
      //   html: '<i class="bi bi-pc-display-horizontal"></i> Remote Control',
      //   visible: checkDeviceMenuItems(e.row?.node?.data),
      //   onItemClick: () => {
      //     action("remote_control", e.row?.node?.data);
      //   },
      // },
      // {
      //   html: '<i class="bi bi-x-lg"></i> Uninstall Apps',
      //   cssClass: 'menu-item-danger',
      //   beginGroup: true,
      //   visible: checkDeviceMenuItems(e.row?.node?.data),
      //   onItemClick: () => {
      //     action("uninstall_apps", e.row?.node?.data);
      //   },
      // },
      // ,{
      //     html: '<i class="bi bi-info-circle"></i> Device Info',
      //     visible : checkDeviceMenuItems(e.row?.node?.data),
      //     onItemClick: () => {
      //       action('get_device_info', e.row?.node?.data);
      //     }
      // }
      {
        html: '<i class="bi bi-trash3"></i> De-register',
        cssClass: 'menu-item-danger',
        onItemClick: () => {
          action("deregister_device", e.row?.node?.data);
        },
      }
      // {
      //   html: '<i class="bi bi-arrow-repeat"></i> Refresh Remote Integration',
      //   visible:
      //     process.env.VUE_APP_SUPER_ADMIN_EMAIL == userEmail.value.user.email,
      //   onItemClick: () => {
      //     action("refresh_get_screen", e.row?.node?.data);
      //   },
      // }
    );
  }
};

const fetchAgentsSimple = () => {
  agentsStore
    .fetchAgentsSimple(companyId.value)
    .then((res) => {
      agentsOptions.value = res.data;
    })
    .catch(() => {
    });
};

const updateSitePassword = async (data) => {
  //  await resetStoreData();
  await action("set_site_password", data);
};

const showPasswordHistory = async (data) => {
  //  await resetStoreData();
  await action("password_history", data);
};

const fetchStoresGrid = () => {
  sitesStore
    .fetchSitesGrid(companyId.value)
    .then((res) => {
      dataTreeGrid.value.endCustomLoading();
      if (res.data && res.data.response) {
        sitesGrid.value = res.data.response.map((row) => {
          row.num_areas = row.area_count ? row.area_count : "-";
          row.num_devices = row.device_count ? row.device_count : "-";
          row.description = row.description ? row.description : "-";
          row.integration_name = row.integration ? row.integration.name : "-";
          // row.contact_email = row.contact_email ? row.contact_email : "-";
          row.timezone = row.timezone ? row.timezone : "-";
          // row.android_serial = row.android_serial ? row.android_serial : "-";
          // row.lamasatech_serial = row.lamasatech_serial ? row.lamasatech_serial : "-";
          row.mac_address = row.mac_address ? row.mac_address : "-";
          row.is_offline = row.last_seen && !row.is_online ? 1 : 0;
          row.config_name = row.configuration ? row.configuration.name : "-";
          row.agent_name = row.agent ? row.agent.name : "-";
          row.status =
            row.data_type == "device"
              ? row.is_online
                ? "Online"
                : row.last_seen && row.is_offline
                ? `Offline since ${moment(row.last_seen).format(
                    "DD/MM/YYYY"
                  )} at ${moment(row.last_seen).format("hh:mm A")}`
                : "-"
              : "-";
          return row;
        });
      }

    })
    .catch(() => {
    });
};

onMounted(async () => {
  dataTreeGrid.value.beginCustomLoading();

});

onBeforeMount(() => {
  fetchStatistics();
  fetchAgentsSimple();
  fetchAllSites();
  appsStore
    .fetchAppsLibrary(companyId.value)
    .then((res) => {
      appsOptions.value = res.data.data;
    })
    .catch(() => {});

  configsStore
    .fetchConfigsGrid(companyId.value)
    .then((res) => {
      configsOptions.value = res.data;
    })
    .catch(() => {});

  fetchStoresGrid();
});
</script>

<template>
  <AgentLayout>
    <checkUserPassword
      v-if="getModalState"
      @updateSitePassword="updateSitePassword"

    />

    <addStore
      v-model:visible="addStoreVisible"
      @refresh="refreshTreeListGrid"
      @refreshStoresArr="fetchAllSites"
    />
    <editStore
      v-if="getModalState"
      @refreshStoresArr="fetchAllSites"
      @refresh="refreshTreeListGrid"
    />
    <deleteStore v-if="getModalState" @refresh="refreshTreeListGrid" />

    <setSitePassword
      v-if="getModalState"
      @refresh="refreshTreeListGrid"
      @showPasswordHistory="showPasswordHistory"
    />
    <passwordHistory
      v-if="getModalState"
      @refresh="refreshTreeListGrid"

    />

    <addLocation
      v-if="getModalState"
      @refresh="refreshTreeListGrid"
      :closeModal="locationModal"
      :storesOptions="sitesOptions"
    />
    <editLocation
      v-if="getModalState"
      @refresh="refreshTreeListGrid"
      :storesOptions="sitesOptions"
    />
    <moveLocation
      v-if="getModalState"
      @refresh="refreshTreeListGrid"
      :sitesOptions="sitesOptions"
    />
    <deleteLocation v-if="getModalState" @refresh="refreshTreeListGrid" />

    <addDevice
      v-if="getModalState"
      @addDeviceRefresh="refreshTreeList"

      :storesOptions="sitesOptions"
      :agentsOptions="agentsOptions"
      :configsOptions="configsOptions"
    />

    <editDevice
      v-if="getModalState"
      @refresh="refreshTreeListGrid"
      :configsOptions="configsOptions"
      :agentsOptions="agentsOptions"
    />
    <!-- <addDeviceWithDetails v-if="getModalState" @refresh="refreshTreeListGrid" :closeModal="deviceModal" :sitesOptions="sitesOptions"/> -->
    <!-- <addDeviceWithGenerateToken v-if="getModalState" @refresh="refreshTreeListGrid" :closeModal="addDeviceWithGenerateTokenModal" :sitesOptions="sitesOptions"/> -->
    <openDoor v-if="getModalState" @refresh="refreshTreeListGrid" />
    <restartDevice v-if="getModalState" @refresh="refreshTreeListGrid" />
    <deviceSettings v-if="getModalState" @refresh="refreshTreeListGrid" />
    <restartApp
      v-if="getModalState"
      @refresh="refreshTreeListGrid"
      :appsOptions="appsOptions"
    />
    <installApp
      v-if="getModalState"
      @refresh="refreshTreeListGrid"
      :appsOptions="appsOptions"
    />
    <uninstallApps
      v-if="getModalState"
      @refresh="refreshTreeListGrid"
      :appsOptions="appsOptions"
    />
    <!-- <clearAppData v-if="getModalState" @refresh="refreshTreeListGrid" :appsOptions="appsOptions"/> -->
    <refreshFaceLibrary v-if="getModalState" @refresh="refreshTreeListGrid" />
    <deRegisterDevice v-if="getModalState" @refresh="refreshTreeListGrid" />
    <moveDevice
      v-if="getModalState"
      @refresh="refreshTreeListGrid"
      :sitesOptions="sitesOptions"
    />
    <deviceInfo v-if="getModalState" @refresh="refreshTreeListGrid" />
    <previewLogs v-if="getModalState" @refresh="refreshTreeListGrid" />
    <deviceApps v-if="getModalState" @refresh="refreshTreeListGrid" />
    <remoteControl v-if="getModalState" @refresh="refreshTreeListGrid" />
    <deviceToken
      v-if="getModalState"

    />
    <deviceConfiguration
      v-if="getModalState"
      @refresh="refreshTreeListGrid"
      :configsOptions="configsOptions"
    />

    <applyJob v-if="getModalState" />

    <PageHeader
      title="Sites & Devices"
      subtitle="A hierarchical management page for Sites → Locations → Devices, where users can manage sites, move locations between sites, move devices between locations, and manage all device-related actions"
      :showButton="false"
    >
      <template #actions>
        <div style="position: relative">
          <div v-if="addMenuOpen" class="fixed inset-0" style="z-index: 99" @click="addMenuOpen = false"></div>
          <button class="btn btn-primary" type="button" @click="addMenuOpen = !addMenuOpen">
            Add <i class="bi bi-chevron-down ml-1"></i>
          </button>
          <div v-if="addMenuOpen" class="dropdown-menu-custom" style="right:0; top: calc(100% + 4px); z-index: 100">
            <button class="dropdown-item-custom" type="button" @click="storeModal('show'); addMenuOpen = false">
              <i class="bi bi-building mr-2"></i>Site
            </button>
            <hr />
            <button class="dropdown-item-custom" type="button" @click="locationModal('show'); addMenuOpen = false">
              <i class="bi bi-geo-alt mr-2"></i>Location
            </button>
            <hr />
            <button
              class="dropdown-item-custom"
              type="button"
              :title="checkLoggedInUserRole() ? 'You are not authorized to perform this action!' : ''"
              :disabled="checkLoggedInUserRole()"
              @click="!checkLoggedInUserRole() && (kioskModal('show'), addMenuOpen = false)"
            >
              <i class="bi bi-tablet mr-2"></i>Device
            </button>
          </div>
        </div>
      </template>
    </PageHeader>

    <div class="statistics-section mt-20">
      <div class="statistics-cards-container">
        <div class="statistics-card statistics-card--estate">
          <div class="stats-card-content">
            <div class="stats-card-label">ESTATE</div>
            <div class="stats-card-value">
              <span class="stats-card-number">{{ totalSites ?? 0 }}</span>
              <span class="stats-card-unit">sites</span>
            </div>
            <div class="stats-card-meta">{{ totalDevices }} devices registered</div>
          </div>
          <svg
            class="stats-card-sparkline"
            viewBox="0 0 140 40"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <polyline
              points="0,30 18,24 32,28 50,18 68,22 86,12 104,16 122,8 140,10"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <div class="statistics-card statistics-card--online">
          <div class="stats-card-label">
            <span class="stats-card-dot stats-card-dot--online"></span>
            ONLINE
          </div>
          <div class="stats-card-value">
            <span class="stats-card-number">{{ totalOnlineDevices }}</span>
            <span class="stats-card-unit">/{{ totalDevices }}</span>
          </div>
          <div class="stats-card-meta">
            <i class="bi bi-arrow-down stats-card-trend stats-card-trend--down"></i>
            <span class="stats-card-trend stats-card-trend--down">
              {{ totalDevices > 0 ? Math.round((totalOnlineDevices / totalDevices) * 100) : 0 }}%
            </span>
            <span class="stats-card-meta-muted">24h</span>
          </div>
          <div class="stats-card-bar">
            <div
              class="stats-card-bar-fill stats-card-bar-fill--online"
              :style="`width: ${totalDevices > 0 ? (totalOnlineDevices / totalDevices) * 100 : 0}%`"
            ></div>
          </div>
        </div>

        <div class="statistics-card statistics-card--offline">
          <div class="stats-card-label">
            <span class="stats-card-dot stats-card-dot--offline"></span>
            OFFLINE
          </div>
          <div class="stats-card-value">
            <span class="stats-card-number stats-card-number--offline">{{ totalOfflineDevices }}</span>
            <span class="stats-card-unit">/{{ totalDevices }}</span>
          </div>
          <div class="stats-card-meta">
            <span class="stats-card-incidents">{{ totalOfflineDevices }} incidents</span>
            <span class="stats-card-meta-sep">·</span>
            <!-- <a class="stats-card-link" href="#">view <span aria-hidden="true">→</span></a> -->
          </div>
          <div class="stats-card-bar">
            <div
              class="stats-card-bar-fill stats-card-bar-fill--offline"
              :style="`width: ${totalDevices > 0 ? (totalOfflineDevices / totalDevices) * 100 : 0}%`"
            ></div>
          </div>
        </div>

        <div class="statistics-card statistics-card--pending">
          <div class="stats-card-label">
            <span class="stats-card-dot stats-card-dot--pending"></span>
            PENDING
          </div>
          <div class="stats-card-value">
            <span class="stats-card-number">{{ totalPendingDevices }}</span>
            <span class="stats-card-unit">/{{ totalDevices }}</span>
          </div>
          <div class="stats-card-meta stats-card-meta-muted">
            {{ totalPendingDevices > 0 ? `${totalPendingDevices} awaiting` : 'No change' }}
            <span class="stats-card-meta-sep">·</span>
            {{ totalPendingDevices > 0 ? 'review' : 'idle' }}
          </div>
          <div class="stats-card-bar">
            <div
              class="stats-card-bar-fill stats-card-bar-fill--pending"
              :style="`width: ${totalDevices > 0 ? (totalPendingDevices / totalDevices) * 100 : 0}%`"
            ></div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showMassAction" class="mass-actions-bar">
      <div class="mass-actions-info">
        <span class="mass-actions-info-pill">
          <strong>{{ selectedDevicesCount }}</strong> selected ·
          {{ visibleDevicesCount }} visible
        </span>
      </div>
      <div v-if="!showSideInfo" class="mass-actions-group">
        <!-- Restart, Screenshot, Configure, Install app and New job are
             intentionally commented out — only Move and De-register are
             exposed. Uncomment to restore; `mass-action-btn--last-merged`
             below must move back to New job so the merged pill keeps its
             rounded right edge. -->
        <!-- <button
          :disabled="isSelectedDevices"
          @click="getSelectedData('mass_restart_devices')"
          type="button"
          class="mass-action-btn"
        >
          <i class="bi bi-arrow-counterclockwise"></i>
          <span>Restart</span>
        </button>

        <button
          :disabled="isSelectedDevices"
          @click="getSelectedData('mass_screenshots')"
          type="button"
          class="mass-action-btn"
        >
          <i class="bi bi-camera"></i>
          <span>Screenshot</span>
        </button> -->

        <button
          :disabled="isSelectedDevices"
          @click="getSelectedData('mass_move_devices')"
          type="button"
          class="mass-action-btn mass-action-btn--last-merged"
        >
          <i class="bi bi-arrow-left-right"></i>
          <span>Move</span>
        </button>

        <!-- <button
          :disabled="isSelectedDevices"
          @click="getSelectedData('mass_devices_config')"
          type="button"
          class="mass-action-btn"
        >
          <i class="bi bi-gear"></i>
          <span>Configure</span>
        </button>

        <button
          :disabled="isSelectedDevices"
          @click="getSelectedData('mass_install_app')"
          type="button"
          class="mass-action-btn"
        >
          <i class="bi bi-download"></i>
          <span>Install app</span>
        </button>

        <button
          :disabled="isSelectedDevices"
          @click="getSelectedData('mass_apply_job')"
          type="button"
          class="mass-action-btn mass-action-btn--last-merged"
        >
          <i class="bi bi-list"></i>
          <span>New job</span>
        </button> -->

        <button
          :disabled="isSelectedDevices"
          @click="getSelectedData('mass_deregister_devices')"
          type="button"
          class="mass-action-btn mass-action-btn--danger"
        >
          <i class="bi bi-trash3"></i>
          <span>De-register</span>
        </button>
      </div>
      <div class="mass-actions-spacer"></div>
    </div>

    <section
      class="grid-white-section sites-grid main-page-container-animation card"
    >
      <div class="flex w-full p-0">
        <div class="p-0" :class="showSideInfo ? 'w-2/3' : 'w-full'">
          <DxTreeList height="100%"
            id="treelist"
            ref="dataGridTreeRefName"
            :data-source="sitesGrid"
            :show-borders="false"
            :show-column-lines="false"
            :show-row-lines="true"
            :column-auto-width="true"
            :word-wrap-enabled="true"
            :allowColumnResizing="true"
            :row-alternation-enabled="false"
            :two-way-binding-enabled="true"
            :allow-column-reordering="true"
            :focused-row-enabled="true"
            :focused-row-index="0"
            :expanded-row-keys="expandedRowKeys"
            :selected-row-keys="selectedRowKeys"
            key-expr="id"
            parent-id-expr="mod_Parent_ID"
            @content-ready="contentReady($event)"
            @editor-preparing="onEditorPreparing($event)"
            @context-menu-preparing="addMenuItems"
            @focused-row-changed="onFocusedRowChanged"
            @selection-changed="onSelectionChanged"
            @cell-prepared="OnCellPrepared"
            @row-click="onClickedRowChanged"
          >
            >
            <DxStateStoring
              :enabled="true"
              type="custom"
              :customSave="customSave"
              :customLoad="customLoad"
            />

            <DxHeaderFilter :visible="true" />

            <DxSearchPanel
              :width="200"
              :visible="true"
              placeholder="Search ..."
            />

            <DxSelection mode="multiple" :recursive="true" />
            <DxColumnChooser :enabled="true" mode="select" />
            <!-- <DxFilterRow :visible="true" /> -->
            <DxLoadPanel :enabled="true" />

            <!-- start::Toolbar -->
            <DxToolbar>
              <!-- <DxItem location="before" name="columnChooserButton"/> -->
              <!-- <DxItem location="before" name="exportButton" /> -->
              <DxItem location="before" template="columnButtonTemplate" />
              <DxItem location="before" template="exportButtonTemplate" />
              <DxItem location="before" template="clearAllTemplate" />
              <!-- <DxItem location="center" template="openDoorButtonTemplate"/> -->
              <!-- <DxItem location="center" template="restartDeviceButtonTemplate"/> -->
              <!-- <DxItem location="center" template="screenshotButtonTemplate"/> -->
              <!-- <DxItem location="center" template="moveButtonTemplate"/> -->
              <!-- <DxItem location="center" template="applyConfigButtonTemplate"/> -->
              <!-- <DxItem location="center" template="restartAppButtonTemplate"/> -->
              <!-- <DxItem location="center" template="installAppButtonTemplate"/> -->
              <!-- <DxItem location="center" template="uninstallAppButtonTemplate" /> -->
              <!-- <DxItem location="center" template="deleteButtonTemplate" /> -->
              <DxItem location="after" name="searchPanel" />
              <!-- <DxItem location="after" name="exportButton" /> -->
            </DxToolbar>

            <!-- end::Toolbar -->

            <!-- start::scrolling -->
            <DxScrolling mode="standard" :use-native="true" />
            <!-- end::scrolling -->

            <DxColumn
              data-field="name"
              caption="Name"
              cell-template="name-cell"
              :allow-searching="true"
              :allow-reordering="false"
              :allow-exporting="true"
              :allow-header-filtering="false"
              data-type="string"
              :minWidth="300"
            />

            <DxColumn
              data-field="integration_name"
              :visible="true"
              caption="Integration Name"
              :allow-searching="true"
              :allow-header-filtering="true"
              :filter-operations="allowedOperations"
              data-type="string"
            />

            <DxColumn
              data-field="description"
              :visible="false"
              caption="Description"
              :allow-searching="true"
              :allow-header-filtering="false"
              :filter-operations="allowedOperations"
              data-type="string"
            />

            <DxColumn
              data-field="num_areas"
              :visible="false"
              caption="Locations Num"
              :allow-searching="true"
              :allow-header-filtering="false"
              :filter-operations="allowedOperations"
              data-type="number"
            />

            <DxColumn
              data-field="num_devices"
              caption="Devices Num"
              :allow-searching="true"
              :allow-sorting="false"
              :allow-header-filtering="false"
              :filter-operations="allowedOperations"
              data-type="number"
            />

            <!-- <DxColumn
            data-field="contact_email"
            :visible="false"
            caption="Contact Email"
            :allow-searching="true"
            :allow-header-filtering="false"
            :filter-operations="allowedOperations"
            data-type="string"
          /> -->

            <DxColumn
              data-field="agent_name"
              caption="Agent"
              :allow-searching="true"
              :allow-sorting="false"
              :allow-header-filtering="true"
              :filter-operations="allowedOperations"
              data-type="string"
            />

            <!-- <DxColumn
            data-field="timezone"
            caption="Time Zone"
            :allow-searching="true"
            :allow-sorting="false"
            :allow-header-filtering="true"
            :filter-operations="allowedOperations"
            data-type="string"
          /> -->

            <DxColumn
              data-field="status"
              caption="Status"
              cell-template="status-cell"
              :allow-searching="true"
              :allow-header-filtering="false"
              :filter-operations="allowedOperations"
              data-type="string"
            />

            <template #name-cell="{ data }">
              <span
                v-if="
                  data.data.data_type == 'site' || data.data.data_type == 'area'
                "
              >
                {{ data.data.name }}</span
              >
              <span v-if="data.data.data_type == 'device'" class="flex">
                <i
                  @click="action('preview_logs', data.data)"
                  class="cursor-pointer"
                  :title="
                    data.data.jobs_status == 'succeeded'
                      ? 'Succeeded'
                      : data.data.jobs_status == 'failed'
                      ? 'Failed'
                      : data.data.jobs_status == 'pending'
                      ? 'Pending'
                      : 'No Jobs'
                  "
                  :class="
                    data.data.jobs_status == 'succeeded'
                      ? 'bi bi-check-circle-fill succeeded-status'
                      : data.data.jobs_status == 'failed'
                      ? 'bi bi-x-circle-fill failed-status'
                      : data.data.jobs_status == 'pending'
                      ? 'bi bi-exclamation-circle-fill pending-status'
                      : 'bi bi-record-fill'
                  "
                ></i>
                <span class="ml-1"> {{ data.data.name }}</span>
              </span>
            </template>

            <template #status-cell="{ data }">
              <span
                :class="
                  data.data.is_online
                    ? 'online-status'
                    : data.data.is_offline
                    ? 'offline-status'
                    : ''
                "
                >{{ data.data.status }}</span
              >
            </template>

            <template #exportButtonTemplate>
              <!-- <DxDropDownButton  class="exportGridHeader" :items="buttonData" text="Export" @item-click="onItemClick" /> -->
              <button
                @click="onItemClick('excel')"
                type="button"
                class="btn icon-button m-5 font-20"
              >
                <i class="bi bi-filetype-xlsx"></i>
              </button>
            </template>

            <template #columnButtonTemplate>
              <!-- <button @click="showColumnChooser" class="custom-columns mx-1">
              <span class="custom-text">Columns </span>
              <span class="custom-icon">
                <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
              </span>
            </button> -->

              <button
                @click="showColumnChooser"
                type="button"
                class="btn icon-button font-20 m-5"
              >
                <i class="bi bi-columns"></i>
              </button>
            </template>

            <template #clearAllTemplate>
              <div v-if="showClearAll" class="clear-cols">
                <div class="custom-line"></div>

                <!-- <Button
                text="Clear All"
                @click="clearFilter()"
                styling-mode="outlined"
                type="danger"
                class="clear-colums-filter"
              /> -->

                <button
                  @click="clearFilter()"
                  type="button"
                  class="btn action-btn clear-colums-filter m-5"
                >
                  Clear Filters
                </button>
              </div>
            </template>

            <!-- <template #openDoorButtonTemplate>
            <button @click="getSelectedData('open-door')" type="button" id="openDoorMassAction" class="btn action-btn m-5"><i class="bi bi-door-open"></i> 
              <DxTooltip :target="'#openDoorMassAction'" show-event="mouseenter" hide-event="mouseleave" :position="'top'"
               :hide-on-outside-click="false">Open Door</DxTooltip>
            </button>
          </template> -->

            <!-- <template #restartDeviceButtonTemplate>
            <button @click="getSelectedData('mass_restart_devices')" type="button" id="restartDeviceMassAction" class="btn action-btn m-5 font-14 ">
              <i class="bi bi-phone-flip"></i> Restart
              <DxTooltip :target="'#restartDeviceMassAction'" show-event="mouseenter" hide-event="mouseleave" :position="'top'"
              :hide-on-outside-click="false">Restart Device</DxTooltip>
            </button>
          </template> -->

            <!-- <template #screenshotButtonTemplate>
            <button @click="getSelectedData('mass_screenshots')" type="button" id="screenshotMassAction" class="btn action-btn m-5 font-14 ">
              <i class="bi bi-fullscreen"></i> Screenshot
              <DxTooltip :target="'#screenshotMassAction'" show-event="mouseenter" hide-event="mouseleave" :position="'top'"
              :animation="animationConfigFade" :hide-on-outside-click="false">Screenshot</DxTooltip>
            </button>
          </template> -->

            <!-- <template #moveButtonTemplate>
            <button @click="getSelectedData('mass_move_devices')" type="button" id="moveDeviceMassAction" class="btn action-btn m-5 font-14 ">
              <i class="bi bi-arrow-left-right"></i> Move
              <DxTooltip :target="'#moveDeviceMassAction'" show-event="mouseenter" hide-event="mouseleave" :position="'top'"
              :animation="animationConfig" :hide-on-outside-click="false">Move Device</DxTooltip>
            </button>
          </template> -->

            <!-- <template #applyConfigButtonTemplate>
            <button @click="getSelectedData('mass_devices_config')" type="button" id="applyConfigDeviceMassAction" class="btn action-btn m-5 font-14 ">
              <i class="bi bi-gear"></i> Config
              <DxTooltip :target="'#applyConfigDeviceMassAction'" show-event="mouseenter" hide-event="mouseleave" :position="'top'"
              :animation="animationConfig" :hide-on-outside-click="false">Apply Config</DxTooltip>
            </button>
          </template> -->

            <!-- <template #restartAppButtonTemplate>
            <button @click="getSelectedData('mass_restart_app')" type="button" id="restartAppMassAction" class="btn action-btn m-5 font-14 ">
              <i class="bi bi-arrow-counterclockwise"></i> Restart App
              <DxTooltip :target="'#restartAppMassAction'" show-event="mouseenter" hide-event="mouseleave" :position="'top'"
              :animation="animationConfig" :hide-on-outside-click="false">Restart App</DxTooltip>
            </button>
          </template> -->

            <!-- <template #installAppButtonTemplate>
            <button @click="getSelectedData('mass_install_app')" type="button" id="installAppMassAction" class="btn action-btn m-5 font-14 ">
              <i class="bi bi-download"></i> Install App
              <DxTooltip :target="'#installAppMassAction'" show-event="mouseenter" hide-event="mouseleave" :position="'top'"
              :animation="animationConfig" :hide-on-outside-click="false">Install App</DxTooltip>
            </button>
          </template> -->

            <!-- <template #uninstallAppButtonTemplate>
            <button type="button" id="uninstallAppMassAction" class="btn action-btn m-5"><i class="bi bi-slash-circle"></i>
              <DxTooltip :target="'#uninstallAppMassAction'" show-event="mouseenter" hide-event="mouseleave" :position="'top'"
              :animation="animationConfig" :hide-on-outside-click="false">Uninstall App</DxTooltip>
            </button>
          </template> -->

            <!-- <template #deleteButtonTemplate>
            <button  @click="getSelectedData('mass_deregister_devices')" type="button" id="deregisterDeviceMassAction" class="btn action-btn m-5 font-14 ">
              <i class="bi bi-trash3"></i> De-register
              <DxTooltip :target="'#deregisterDeviceMassAction'" show-event="mouseenter" hide-event="mouseleave" :position="'top'"
              :animation="animationConfig" :hide-on-outside-click="false">De-register</DxTooltip>
            </button>
          </template> -->
          </DxTreeList>
        </div>
        <div v-if="showSideInfo" class="w-1/3 kiosk-actions-container">
          <span @click="closeSideInfo()" class="kiosk-action-bar-close-btn">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
            >
              <path
                d="M11 0C4.91333 0 0 4.91333 0 11C0 17.0867 4.91333 22 11 22C17.0867 22 22 17.0867 22 11C22 4.91333 17.0867 0 11 0ZM16.5978 14.7156C17.1111 15.2289 17.1111 16.06 16.5978 16.5733C16.3289 16.8422 16.0111 16.9644 15.6689 16.9644C15.3267 16.9644 14.9844 16.8422 14.74 16.5733L11.0733 12.9067L7.38222 16.5733C7.11333 16.8422 6.79556 16.9644 6.45333 16.9644C6.11111 16.9644 5.76889 16.8422 5.52444 16.5733C5.01111 16.06 5.01111 15.2289 5.52444 14.7156L9.21556 11.0244L5.52444 7.35778C5.01111 6.84444 5.01111 6.01333 5.52444 5.5C6.03778 4.98667 6.86889 4.98667 7.38222 5.5L11.0733 9.19111L14.7644 5.5C15.2778 4.98667 16.1089 4.98667 16.6222 5.5C17.1356 6.01333 17.1356 6.84444 16.6222 7.35778L12.9311 11.0489L16.5978 14.7156Z"
                fill="#F46A6A"
              />
            </svg>
          </span>

          <!-- Start :: kiosk actions -->
          <section
            class="actions-item-container info-white-section"
            style="height: fit-content; overflow: hidden"
          >
            <template v-if="hideSkeletor">
              <div v-for="(value, key) in availableKioskActions" :key="key">
                <div
                  class="actions-item"
                  @click="action(value.action, deviceDataObj)"
                >
                  <i :class="`bi ${value.icon}`"></i>
                  <p>{{ value.text }}</p>
                </div>
              </div>
            </template>
            <template v-else>
              <KioskActionsBarSkeletor />
            </template>
          </section>
          <!-- End :: kiosk actions -->

          <!-- Start:: Kiosk data -->
          <!-- Start:: Kiosk info -->
          <template v-if="deviceData">
            <section
              class="info-white-section pa-10"
              style="height: fit-content; overflow: hidden; margin-top: 10px"
            >
              <div class="mb-15">
                <h5 v-if="hideSkeletor" class="underline section-header">
                  Device Info
                </h5>
                <Skeletor v-else width="150" as="div" />
              </div>

              <div class="device-info-container">
                <div class="kiosk-info-section w-full">
                  <template v-if="hideSkeletor">
                    <template
                      v-for="(value, key) in deviceData.deviceInfo"
                      :key="key"
                    >
                      <div
                        v-for="(value, key) in value"
                        :key="key"
                        class="device-info-row flex"
                      >
                        <p class="kiosk-keys w-1/3 p-0">
                          {{ `${key.charAt(0).toUpperCase()}${key.slice(1)}` }}
                        </p>
                        <p class="device-info-value w-2/3 p-0">{{ value }}</p>
                      </div>
                    </template>
                  </template>

                  <template v-else>
                    <DeviceInfoSkeletor />
                  </template>
                </div>
              </div>
            </section>
            <!-- End:: Kiosk info -->

            <!-- Start:: Network -->
            <!-- <section class="info-white-section pa-10" style="height: fit-content; overflow: hidden; margin-top: 10px;">
              <div class="mb-15">
                <h5  v-if="hideSkeletor" class="underline section-header">Netowrk</h5>
                <Skeletor v-else width="150" as="div" />
              </div>
                <div class="device-info-container">    
                  <div class="kiosk-info-section w-full">
                    <template v-if="hideSkeletor">
                      <template  v-for="(value,key) in deviceData.network" :key="key">
                        <div v-for="(value,key) in value" :key="key" class="device-info-row flex">
                          <p class="kiosk-keys w-1/3 p-0">{{ `${key.charAt(0).toUpperCase()}${key.slice(1)}` }}</p>
                          <p class="device-info-value w-2/3 p-0"> {{ value }}</p>
                        </div>
                      </template>
                    </template>

                    <template v-else>
                        <DeviceInfoSkeletor/>
                    </template>
                  </div>
                </div>
              </section> -->
            <!-- End:: Network -->

            <!-- Start:: Disk -->
            <section class="white-bg disk-section">
              <div class="mb-15">
                <h5 v-if="hideSkeletor" class="underline section-header">
                  Disk
                </h5>
                <Skeletor v-else width="150" as="div" />
              </div>
              <!-- <p class="section-info">Total Memory: {{ deviceData.disk?.total }}</p> -->

              <div v-if="hideSkeletor">
                <div class="disk-stats">
                  <p class="text-center">
                    Total Disk Space:
                    {{ (deviceData.disk?.total / 1024).toFixed(1) }} GB
                  </p>

                  <DiskChart :diskData="deviceData.disk" />

                  <div class="disk-memory-stats">
                    <div class="flex">
                      <div class="used-space-stats"></div>
                      <p>
                        Used Disk Space:
                        {{ (deviceData.disk?.used / 1024).toFixed(1) }} GB
                      </p>
                    </div>

                    <div class="flex">
                      <div class="available-space-stats"></div>
                      <p>
                        Available Disk Space:
                        {{ (deviceData.disk?.available / 1024).toFixed(1) }} GB
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <template v-else>
                <DiskInfoSkeletor />
              </template>
            </section>
            <!-- End:: Disk -->

            <section class="flex white-bg mt-10 pa-10 battery-memory-section">
              <!-- Start:: Battery -->
              <div
                class="battery-section"
                style="height: fit-content; overflow: hidden; margin-top: 10px"
              >
                <div class="mb-2">
                  <h5 v-if="hideSkeletor" class="underline section-header">
                    Battery
                  </h5>
                  <Skeletor v-else width="120" as="div" />
                </div>

                <div v-if="hideSkeletor">
                  <div v-if="deviceData.battery">
                    <p class="section-info">Main Battery</p>

                    <div class="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                      <div class="bg-green-500 h-4 rounded-full transition-all flex items-center justify-center"
                           :style="{ width: deviceData.battery + '%' }">
                        <span class="text-xs text-white px-2">{{ deviceData.battery }}%</span>
                      </div>
                    </div>
                  </div>

                  <div v-else class="no-battery-message">
                    <p>Not Detected</p>
                  </div>
                </div>
                <template v-else>
                  <BatterySkeletor />
                </template>
              </div>
              <!-- End:: Battery -->

              <div class="vertical-line"></div>

              <!-- Start:: Memory -->
              <div
                v-if="deviceData.memory"
                class="memory-section"
                style="height: fit-content; overflow: hidden; margin-top: 10px"
              >
                <div class="mb-2">
                  <h5 v-if="hideSkeletor" class="underline section-header">
                    Memory
                  </h5>
                  <Skeletor v-else width="120" as="div" />
                </div>
                <div v-if="hideSkeletor">
                  <p class="section-info">Memory Usage</p>

                  <div class="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                    <div class="bg-green-500 h-4 rounded-full transition-all flex items-center justify-center"
                         :style="{ width: (deviceData.memory.used / deviceData.memory.total * 100) + '%' }">
                      <span class="text-xs text-white px-2">{{ deviceData.memory.percentage }}%</span>
                    </div>
                  </div>
                  <div class="memory-stats-container">
                    <p class="m-0">
                      {{ deviceData.memory.used }}MB <b>used</b> memory
                    </p>
                    <p class="m-0">
                      {{ deviceData.memory.total - deviceData.memory.used }}MB
                      <b>free</b> of {{ deviceData.memory.total }}MB
                    </p>
                  </div>
                </div>

                <template v-else>
                  <MemorySkeletor />
                </template>
              </div>
              <!-- End:: Memory -->
            </section>
          </template>
          <!-- End :: Kiosk data -->
        </div>
      </div>
    </section>
  </AgentLayout>
</template>

<style>
.sites-grid {
  p {
    font-size: 12px;
    line-height: 25px;
  }
}
.kiosk-actions-container {
  margin-top: -25px;
  height: 80.7vh;
  overflow: auto;
}
.actions-item-container {
  display: flex;
  justify-content: space-evenly;
  column-gap: 30px;
  padding: 5px 20px;
}
.battery-section {
  margin-right: 20px;
}
.memory-section {
  margin-left: 20px;
}
.battery-section,
.memory-section {
  flex-grow: 1;
  flex-basis: 0;
  min-width: 0;
}
.actions-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 5px 5px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 23px !important;

  p {
    margin: 0;
  }
  .bi-phone-flip::before,
  .bi-columns-gap::before,
  .bi-pc-display-horizontal::before,
  .bi-arrow-repeat::before {
    font-size: 23px;
  }
}
.kiosk-info-section p {
  margin: 0;
}
.kiosk-keys {
  font-weight: 600;
}
.device-info-container {
  display: flex;
  justify-content: space-between;
}

.actions-item:hover {
  color: #198754;
}
.memory-stats-container {
  width: fit-content;
  margin: auto;
}
.underline {
  text-decoration: underline;
}
.section-header {
  font-size: 14px;
}

.section-info {
  margin: 0;
  font-weight: 600;
}
.disk-stats {
  margin-top: 15px;
}

.used-space-stats {
  width: 21px;
  height: 23px;
  background-color: #377eb8;
  margin-right: 10px;
}
.available-space-stats {
  margin-right: 10px;
  width: 21px;
  height: 23px;
  background-color: #4daf4a;
}
.kiosk-action-bar-close-btn {
  display: flex;
  flex-direction: row-reverse;
  cursor: pointer;
}
.kiosk-action-bar-close-btn svg {
  transform: translateY(15px) translateX(8px);
}
.progress-total-bg {
  background-color: var(--neutral-300);
}
.white-bg {
  background: var(--neutral-0) !important;
}
.vertical-line {
  border-left: 1px solid var(--neutral-300);
}
.total-memory-label {
  margin: 0 0 15px 28px;
}
.device-info-value {
  word-break: break-all;
  width: 60%;
  padding-right: 35px;
}
.battery-memory-section {
  border-radius: 8px;
}
.disk-section {
  height: fit-content;
  overflow: hidden !important;
  padding: 10px 10px 0 10px;
  margin-top: 10px;
  border-radius: 8px;
}
.disk-memory-stats {
  display: flex;
  flex-direction: column;
  width: fit-content;
  margin: 15px auto;
}
.no-battery-message {
  height: auto;
  display: flex;
  justify-content: center;
  color: var(--neutral-600);
  font-family: Lato;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.statistics-section {
  margin-bottom: 20px;
}

.statistics-cards-container {
  display: grid;
  grid-template-columns: 403px repeat(3, 1fr);
  gap: 0;
  margin-bottom: 20px;
}

.statistics-card {
  position: relative;
  background: var(--neutral-0);
  border: 1px solid var(--neutral-200);
  border-radius: 0;
  padding: 18px 24px 18px;
  height: 149px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
}

.statistics-card + .statistics-card {
  border-left: none;
}

.statistics-card:first-child {
  border-top-left-radius: 14px;
  border-bottom-left-radius: 14px;
}

.statistics-card:last-child {
  border-top-right-radius: 14px;
  border-bottom-right-radius: 14px;
}

.statistics-card:hover {
  border-color: var(--neutral-300);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  z-index: 1;
}

.stats-card-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--neutral-500);
  margin-bottom: 12px;
  text-transform: uppercase;
}

.stats-card-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;
}

.stats-card-dot--online {
  background: var(--primary-color);
}

.stats-card-dot--offline {
  background: #dc2626;
}

.stats-card-dot--pending {
  background: #f59e0b;
}

.stats-card-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 10px;
}

.stats-card-number {
  font-size: 38px;
  font-weight: 700;
  line-height: 1;
  color: var(--neutral-900);
  letter-spacing: -0.01em;
}

.stats-card-number--offline {
  color: #dc2626;
}

.stats-card-unit {
  font-size: 14px;
  font-weight: 500;
  color: var(--neutral-400);
  line-height: 1;
}

.stats-card-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--neutral-600);
  flex-wrap: wrap;
}

.stats-card-meta-muted {
  color: var(--neutral-400);
}

.stats-card-meta-sep {
  color: var(--neutral-300);
}

.stats-card-trend {
  display: inline-flex;
  align-items: center;
  font-weight: 500;
  font-size: 13px;
}

.stats-card-trend--down {
  color: #dc2626;
}

.stats-card-incidents {
  color: #dc2626;
  font-weight: 500;
}

.stats-card-link {
  color: var(--neutral-600);
  text-decoration: underline;
  text-underline-offset: 2px;
  font-weight: 500;
}

.stats-card-link:hover {
  color: var(--neutral-900);
}

.stats-card-bar {
  margin-top: auto;
  height: 4px;
  background: var(--neutral-100);
  border-radius: 999px;
  overflow: hidden;
}

.stats-card-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.stats-card-bar-fill--online {
  background: var(--primary-color);
}

.stats-card-bar-fill--offline {
  background: #dc2626;
}

.stats-card-bar-fill--pending {
  background: #f59e0b;
}

.statistics-card--estate {
  flex-direction: row;
  align-items: stretch;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
}

.statistics-card--estate .stats-card-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.stats-card-sparkline {
  flex-shrink: 0;
  width: 55%;
  max-width: 180px;
  height: auto;
  align-self: center;
  color: var(--neutral-900);
}

@media (max-width: 1024px) {
  .statistics-cards-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .statistics-card,
  .statistics-card:first-child,
  .statistics-card:last-child {
    border-radius: 14px;
    height: auto;
    min-height: 149px;
  }

  .statistics-card + .statistics-card {
    border-left: 1px solid var(--neutral-200);
  }
}

@media (max-width: 640px) {
  .statistics-cards-container {
    grid-template-columns: 1fr;
  }
}

/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] .statistics-card {
  background: #1e2a3e;
  border-color: #2d3f5a;
}

[data-theme="dark"] .statistics-card:hover {
  border-color: #3d5070;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

[data-theme="dark"] .statistics-card + .statistics-card {
  border-left: none;
}

@media (max-width: 1024px) {
  [data-theme="dark"] .statistics-card + .statistics-card {
    border-left: 1px solid #2d3f5a;
  }
}

[data-theme="dark"] .stats-card-label {
  color: #9aadc4;
}

[data-theme="dark"] .stats-card-number {
  color: #e8edf5;
}

[data-theme="dark"] .stats-card-number--offline {
  color: #f87171;
}

[data-theme="dark"] .stats-card-unit {
  color: #6480a0;
}

[data-theme="dark"] .stats-card-meta {
  color: #9aadc4;
}

[data-theme="dark"] .stats-card-meta-muted {
  color: #6480a0;
}

[data-theme="dark"] .stats-card-meta-sep {
  color: #3d5070;
}

[data-theme="dark"] .stats-card-trend--down {
  color: #f87171;
}

[data-theme="dark"] .stats-card-incidents {
  color: #f87171;
}

[data-theme="dark"] .stats-card-link {
  color: #9aadc4;
}

[data-theme="dark"] .stats-card-link:hover {
  color: #e8edf5;
}

[data-theme="dark"] .stats-card-bar {
  background: #28364e;
}

[data-theme="dark"] .stats-card-sparkline {
  color: #e8edf5;
}
</style>
